import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import Loader from './Loader';
import './LeaveRequests.css';

function LeaveRequests() {
  const { currentUser } = useAuth();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);

  // Leave types
  const leaveTypes = [
    { value: 'casual', label: 'Casual Leave' },
    { value: 'sick', label: 'Sick Leave' },
    { value: 'emergency', label: 'Emergency Leave' }
  ];

  // Form state
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    leaveType: '',
    reason: ''
  });

  // Get today's date for min date
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Fetch leave requests
  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, 'leaves'),
      where('userId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const leaves = [];
      snapshot.forEach((docSnap) => {
        leaves.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      // Sort by createdAt descending
      leaves.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return dateB - dateA;
      });
      setLeaveRequests(leaves);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching leave requests:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ startDate: '', endDate: '', leaveType: '', reason: '' });
  };

  const calculateDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.startDate || !formData.endDate || !formData.leaveType || !formData.reason.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error('End date cannot be before start date');
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, 'leaves'), {
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        userEmail: currentUser.email,
        startDate: formData.startDate,
        endDate: formData.endDate,
        leaveType: formData.leaveType,
        reason: formData.reason.trim(),
        totalDays: calculateDays(formData.startDate, formData.endDate),
        status: 'pending',
        rejectionReason: '',
        createdAt: Timestamp.now()
      });

      resetForm();
      setShowAddModal(false);
      toast.success('Leave request submitted successfully!');
    } catch (error) {
      console.error('Error adding leave request:', error);
      toast.error('Failed to submit leave request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (leave) => {
    if (leave.status !== 'pending') {
      toast.error('You can only edit pending requests');
      return;
    }
    setEditingLeave(leave);
    setFormData({
      startDate: leave.startDate,
      endDate: leave.endDate,
      leaveType: leave.leaveType,
      reason: leave.reason
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!formData.startDate || !formData.endDate || !formData.leaveType || !formData.reason.trim()) {
      toast.error('Please fill all required fields');
      return;
    }

    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error('End date cannot be before start date');
      return;
    }

    setSubmitting(true);

    try {
      await updateDoc(doc(db, 'leaves', editingLeave.id), {
        startDate: formData.startDate,
        endDate: formData.endDate,
        leaveType: formData.leaveType,
        reason: formData.reason.trim(),
        totalDays: calculateDays(formData.startDate, formData.endDate)
      });

      resetForm();
      setShowEditModal(false);
      setEditingLeave(null);
      toast.success('Leave request updated successfully!');
    } catch (error) {
      console.error('Error updating leave request:', error);
      toast.error('Failed to update leave request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (leave) => {
    if (leave.status !== 'pending') {
      toast.error('You can only delete pending requests');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this leave request?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'leaves', leave.id));
      toast.success('Leave request deleted successfully!');
    } catch (error) {
      console.error('Error deleting leave request:', error);
      toast.error('Failed to delete leave request. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="status-badge approved">Approved</span>;
      case 'rejected':
        return <span className="status-badge rejected">Rejected</span>;
      default:
        return <span className="status-badge pending">Pending</span>;
    }
  };

  const getLeaveTypeBadge = (type) => {
    switch (type) {
      case 'sick':
        return <span className="leave-type-badge sick">Sick Leave</span>;
      case 'emergency':
        return <span className="leave-type-badge emergency">Emergency Leave</span>;
      default:
        return <span className="leave-type-badge casual">Casual Leave</span>;
    }
  };

  if (loading) {
    return <Loader message="Loading leave requests..." />;
  }

  return (
    <div className="leave-page">
      {/* Header */}
      <div className="leave-header">
        <div>
          <h1>Leave Requests</h1>
          <p>Apply for leave and track your requests</p>
        </div>
        <button className="btn-add-leave" onClick={() => setShowAddModal(true)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Apply Leave
        </button>
      </div>

      {/* Info Banner */}
      <div className="info-banner">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <span>Submit your leave request and wait for admin approval. You can edit or delete pending requests.</span>
      </div>

      {/* Leave Requests List */}
      <div className="leave-container">
        {leaveRequests.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <h3>No Leave Requests Yet</h3>
            <p>Start by applying for your first leave</p>
          </div>
        ) : (
          <div className="leave-list">
            {leaveRequests.map((leave) => (
              <div key={leave.id} className={`leave-card ${leave.status}`}>
                <div className="leave-card-header">
                  <div className="leave-type-status">
                    {getLeaveTypeBadge(leave.leaveType)}
                    {getStatusBadge(leave.status)}
                  </div>
                  {leave.status === 'pending' && (
                    <div className="leave-actions">
                      <button className="btn-edit" onClick={() => handleEdit(leave)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(leave)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>

                <div className="leave-dates">
                  <div className="date-range">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>{formatDate(leave.startDate)} - {formatDate(leave.endDate)}</span>
                  </div>
                  <div className="total-days">
                    <span className="days-count">{leave.totalDays}</span>
                    <span className="days-label">{leave.totalDays === 1 ? 'Day' : 'Days'}</span>
                  </div>
                </div>

                <div className="leave-reason">
                  <strong>Reason:</strong> {leave.reason}
                </div>

                {leave.status === 'rejected' && leave.rejectionReason && (
                  <div className="rejection-reason">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    <span><strong>Rejection Reason:</strong> {leave.rejectionReason}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Leave Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Apply for Leave</h3>
              <button className="modal-close" onClick={() => { setShowAddModal(false); resetForm(); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Leave Type *</label>
                <select
                  name="leaveType"
                  value={formData.leaveType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Leave Type</option>
                  {leaveTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    min={getTodayDate()}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    min={formData.startDate || getTodayDate()}
                    required
                  />
                </div>
              </div>

              {formData.startDate && formData.endDate && (
                <div className="days-preview">
                  Total Days: <strong>{calculateDays(formData.startDate, formData.endDate)}</strong>
                </div>
              )}

              <div className="form-group">
                <label>Reason *</label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  placeholder="Please provide a reason for your leave request..."
                  rows="3"
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Leave Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Leave Request</h3>
              <button className="modal-close" onClick={() => { setShowEditModal(false); resetForm(); setEditingLeave(null); }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <form onSubmit={handleUpdate}>
              <div className="form-group">
                <label>Leave Type *</label>
                <select
                  name="leaveType"
                  value={formData.leaveType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Leave Type</option>
                  {leaveTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    min={formData.startDate}
                    required
                  />
                </div>
              </div>

              {formData.startDate && formData.endDate && (
                <div className="days-preview">
                  Total Days: <strong>{calculateDays(formData.startDate, formData.endDate)}</strong>
                </div>
              )}

              <div className="form-group">
                <label>Reason *</label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  placeholder="Please provide a reason for your leave request..."
                  rows="3"
                  required
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => { setShowEditModal(false); resetForm(); setEditingLeave(null); }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeaveRequests;
